import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Skull,
  MapPin,
  Swords,
  Flame,
  Sparkles,
  Crosshair,
  FileText,
  AlertTriangle,
  Bot,
  Users,
  EyeOff,
  Eye,
} from "lucide-react";

const TIER_COLORS = {
  S: "bg-gradient-to-br from-yellow-400 to-orange-500 text-black",
  A: "bg-gradient-to-br from-neon to-blue-500 text-black",
  B: "bg-gradient-to-br from-emerald-400 to-teal-500 text-black",
};

const DIFF_COLORS = {
  Beginner: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300",
  Intermediate: "bg-cyan-500/15 border-cyan-500/30 text-cyan-300",
  Advanced: "bg-amber-500/15 border-amber-500/30 text-amber-300",
  Expert: "bg-rose-500/15 border-rose-500/30 text-rose-300",
  New: "bg-fuchsia-500/15 border-fuchsia-500/30 text-fuchsia-300",
};

export default function ZombiesHubPage() {
  const { t } = useT();
  const [hub, setHub] = useState(null);
  const [showGlitches, setShowGlitches] = useState(false);

  useEffect(() => {
    api.get("/zombies/hub").then((r) => setHub(r.data));
  }, []);

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          kicker={
            <span className="inline-flex items-center gap-1.5">
              <Skull size={12} /> {hub?.season || "Zombies"}
            </span>
          }
          title={t("zh.title")}
          subtitle={t("zh.sub")}
        />

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8">
          <Link to="/app/coach">
            <Button className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold h-11 px-5">
              <Bot size={16} className="mr-2" /> {t("zh.ask_coach")}
            </Button>
          </Link>
          <Link to="/app/teammates?game=Call+of+Duty+Zombies">
            <Button
              variant="outline"
              className="rounded-full bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white h-11 px-5"
            >
              <Users size={16} className="mr-2" /> {t("zh.squad")}
            </Button>
          </Link>
        </div>

        {!hub ? (
          <div className="text-center text-zinc-500 py-16">Loading Zombies Hub...</div>
        ) : (
          <Tabs defaultValue="maps" className="w-full">
            <TabsList className="bg-zinc-900/60 border border-white/5 rounded-full p-1 flex flex-wrap h-auto gap-1 w-full justify-start">
              <TabTrig value="maps" icon={MapPin} label={t("zh.tab.maps")} />
              <TabTrig value="ee" icon={Sparkles} label={t("zh.tab.easter_eggs")} />
              <TabTrig value="bosses" icon={Swords} label={t("zh.tab.bosses")} />
              <TabTrig value="high_round" icon={Flame} label={t("zh.tab.high_round")} />
              <TabTrig value="gobblegum" icon={Sparkles} label={t("zh.tab.gobblegums")} />
              <TabTrig value="weapons" icon={Crosshair} label={t("zh.tab.weapons")} />
              <TabTrig value="patch" icon={FileText} label={t("zh.tab.patchnotes")} />
              <TabTrig value="glitches" icon={AlertTriangle} label={t("zh.tab.glitches")} />
            </TabsList>

            {/* MAPS */}
            <TabsContent value="maps" className="mt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {hub.maps.map((m, i) => (
                  <Link
                    key={m.id}
                    to={`/app/zombies/map/${m.id}`}
                    className="card-interactive rounded-2xl overflow-hidden border border-white/5 bg-zinc-900/50 animate-fade-up block hover:border-neon/40 hover:shadow-neon transition-all"
                    style={{ animationDelay: `${i * 50}ms` }}
                    data-testid={`zh-map-${m.id}`}
                  >
                    <div className="relative h-36 overflow-hidden">
                      <img src={m.cover} alt={m.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                      <Badge className={`absolute top-3 right-3 ${DIFF_COLORS[m.difficulty] || DIFF_COLORS.Intermediate}`}>
                        {m.difficulty}
                      </Badge>
                      <Badge className="absolute top-3 left-3 bg-black/70 border-white/10 text-zinc-200 backdrop-blur">
                        {m.game}
                      </Badge>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-lg font-bold">{m.name}</h3>
                      <p className="mt-1 text-sm text-zinc-400">{m.notes}</p>
                      <div className="mt-3 text-xs text-neon font-semibold inline-flex items-center gap-1">
                        Guide öffnen →
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </TabsContent>

            {/* EASTER EGGS */}
            <TabsContent value="ee" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {hub.easter_eggs.map((ee) => (
                  <article
                    key={ee.id}
                    className="rounded-2xl border border-white/5 bg-zinc-900/50 p-5"
                    data-testid={`zh-ee-${ee.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-lg font-bold">{ee.title}</h3>
                      <Badge className="bg-neon/10 border-neon/25 text-neon">{ee.steps} Steps</Badge>
                    </div>
                    <div className="mt-1 text-xs text-zinc-500 inline-flex items-center gap-2">
                      <span>{ee.map}</span> · <span>Team {ee.team_size}</span>
                    </div>
                    <p className="mt-3 text-sm text-zinc-300">{ee.tip}</p>
                    <div className="mt-3 text-xs text-zinc-400">
                      <span className="text-zinc-500">{t("zh.ee_reward")}:</span>{" "}
                      <span className="text-neon font-semibold">{ee.reward}</span>
                    </div>
                  </article>
                ))}
              </div>
              <CoachHint t={t} />
            </TabsContent>

            {/* BOSSES */}
            <TabsContent value="bosses" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {hub.bosses.map((b) => (
                  <article
                    key={b.id}
                    className="rounded-2xl border border-white/5 bg-zinc-900/50 p-5"
                    data-testid={`zh-boss-${b.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-lg font-bold">{b.name}</h3>
                      <Badge className={DIFF_COLORS[b.difficulty] || DIFF_COLORS.Intermediate}>
                        {b.difficulty}
                      </Badge>
                    </div>
                    <div className="text-xs text-zinc-500">{b.map}</div>
                    <div className="mt-3 text-sm">
                      <span className="text-zinc-500">{t("zh.weak")}:</span>{" "}
                      <span className="text-zinc-200">{b.weak_spot}</span>
                    </div>
                    <div className="mt-2 text-sm text-zinc-300">{b.strategy}</div>
                  </article>
                ))}
              </div>
            </TabsContent>

            {/* HIGH ROUND */}
            <TabsContent value="high_round" className="mt-6">
              <ul className="space-y-3">
                {hub.high_round.map((hr, i) => (
                  <li
                    key={hr.id}
                    className="rounded-2xl border border-white/5 bg-zinc-900/50 p-5 flex gap-4"
                    data-testid={`zh-hr-${hr.id}`}
                  >
                    <span className="w-9 h-9 rounded-full bg-neon/15 border border-neon/30 grid place-items-center text-neon font-mono font-bold text-xs shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <div className="font-display font-bold">{hr.title}</div>
                      <p className="text-sm text-zinc-300 mt-1">{hr.tip}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </TabsContent>

            {/* GOBBLEGUMS */}
            <TabsContent value="gobblegum" className="mt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {hub.gobblegums.map((g) => (
                  <article
                    key={g.id}
                    className="rounded-2xl border border-white/5 bg-zinc-900/50 p-5"
                    data-testid={`zh-gg-${g.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-display font-bold">{g.name}</h3>
                      <Badge className="bg-fuchsia-500/15 border-fuchsia-500/30 text-fuchsia-300 text-[10px]">
                        {g.rarity}
                      </Badge>
                    </div>
                    <p className="mt-2 text-sm text-zinc-300">{g.effect}</p>
                    <p className="mt-2 text-xs text-zinc-500">
                      <span className="text-neon font-semibold">Use:</span> {g.use}
                    </p>
                  </article>
                ))}
              </div>
            </TabsContent>

            {/* WEAPONS */}
            <TabsContent value="weapons" className="mt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {hub.weapons.map((w) => (
                  <article
                    key={w.id}
                    className="rounded-2xl border border-white/5 bg-zinc-900/50 p-4 flex items-center gap-4"
                    data-testid={`zh-w-${w.id}`}
                  >
                    <span
                      className={`w-12 h-12 rounded-xl grid place-items-center font-display font-black text-xl ${
                        TIER_COLORS[w.tier] || TIER_COLORS.A
                      }`}
                    >
                      {w.tier}
                    </span>
                    <div className="min-w-0">
                      <div className="font-display font-bold truncate">{w.name}</div>
                      <div className="text-[11px] text-zinc-500">{w.role}</div>
                      <p className="text-sm text-zinc-300 mt-1 line-clamp-2">{w.note}</p>
                    </div>
                  </article>
                ))}
              </div>
              <p className="mt-4 text-xs text-zinc-500" data-testid="zh-meta-note">
                {t("zh.meta_note")}
              </p>
            </TabsContent>

            {/* PATCHNOTES */}
            <TabsContent value="patch" className="mt-6">
              <ul className="space-y-3">
                {hub.patchnotes.map((p) => (
                  <li
                    key={p.id}
                    className="rounded-2xl border border-white/5 bg-zinc-900/50 p-5"
                    data-testid={`zh-patch-${p.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold">{p.version}</div>
                      <span className="text-xs text-zinc-500 font-mono">{p.date}</span>
                    </div>
                    <p className="text-sm text-zinc-300 mt-2">{p.summary}</p>
                  </li>
                ))}
              </ul>
            </TabsContent>

            {/* GLITCHES — nur nach Nachfrage */}
            <TabsContent value="glitches" className="mt-6">
              {!showGlitches ? (
                <div
                  className="rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-zinc-900/60 to-black p-6 sm:p-8 text-center"
                  data-testid="zh-glitches-gate"
                >
                  <AlertTriangle className="text-amber-400 mx-auto" size={28} />
                  <h3 className="mt-3 font-display text-xl sm:text-2xl font-bold">
                    {t("zh.glitch_warn_h")}
                  </h3>
                  <p className="mt-2 text-sm text-zinc-300 max-w-md mx-auto">
                    {t("zh.glitch_warn_sub")}
                  </p>
                  <Button
                    onClick={() => setShowGlitches(true)}
                    className="mt-5 rounded-full bg-amber-400 text-black hover:bg-amber-300 font-bold h-11 px-6"
                    data-testid="zh-reveal-glitches"
                  >
                    <Eye size={16} className="mr-2" /> {t("zh.glitch_show")}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-amber-300 inline-flex items-center gap-2">
                      <AlertTriangle size={14} /> {t("zh.glitch_note")}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowGlitches(false)}
                      className="text-zinc-400 hover:text-white"
                      data-testid="zh-hide-glitches"
                    >
                      <EyeOff size={14} className="mr-1" /> {t("zh.glitch_hide")}
                    </Button>
                  </div>
                  {hub.glitches.map((g) => (
                    <article
                      key={g.id}
                      className="rounded-2xl border border-amber-500/20 bg-zinc-900/50 p-5"
                      data-testid={`zh-glitch-${g.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-display font-bold">{g.type}</div>
                        <Badge className="bg-amber-500/15 border-amber-500/30 text-amber-300 text-[10px]">
                          {g.risk}
                        </Badge>
                      </div>
                      <div className="text-xs text-zinc-500">{g.map}</div>
                      <p className="mt-2 text-sm text-zinc-300">{g.note}</p>
                    </article>
                  ))}
                  <CoachHint t={t} />
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </PageContainer>
    </AppShell>
  );
}

function TabTrig({ value, icon: Icon, label }) {
  return (
    <TabsTrigger
      value={value}
      className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-neon data-[state=active]:text-black data-[state=active]:shadow-neon text-zinc-300"
      data-testid={`zh-tab-${value}`}
    >
      <Icon size={13} className="mr-1.5" />
      {label}
    </TabsTrigger>
  );
}

function CoachHint({ t }) {
  return (
    <div className="mt-5 rounded-2xl border border-neon/20 bg-neon/5 p-4 flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-3">
        <Bot className="text-neon shrink-0" size={18} />
        <p className="text-sm text-zinc-200">{t("zh.coach_hint")}</p>
      </div>
      <Link to="/app/coach">
        <Button size="sm" className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold">
          {t("zh.open_coach")}
        </Button>
      </Link>
    </div>
  );
}
